const PHONE_PATTERN = /^(?:0|\+?27)[6-8]\d{8}$/;

function normalisePhone(value) {
  return value.replace(/[\s\-().]/g, '');
}

function checkoutButtons() {
  return Array.from(document.querySelectorAll('button[name="checkout"], input[name="checkout"]'));
}

/** Charm-rule messages rendered by snippets/maak-charm-rules.liquid that currently block checkout. */
function blockedCharmRules() {
  return Array.from(document.querySelectorAll('maak-charm-rules[data-blocked="true"]')).filter((el) => el.isConnected);
}

/** Holds checkout buttons until every delivery form is ready and no charm rule is broken. */
function syncCheckoutButtons() {
  const allReady =
    [...MaakDeliveryDetails.instances].every((i) => i.ready) && blockedCharmRules().length === 0;
  for (const button of checkoutButtons()) {
    if (allReady) {
      if (button.dataset.maakHeld === 'true') {
        button.disabled = false;
        delete button.dataset.maakHeld;
      }
    } else if (!button.disabled) {
      button.disabled = true;
      button.dataset.maakHeld = 'true';
    }
  }
}

class MaakDeliveryDetails extends HTMLElement {
  #saveTimer = null;
  #saved = false;
  #saving = null;

  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('[data-attr]'));
    this.choices = Array.from(this.querySelectorAll('[data-choice]'));
    this.choiceGroup = this.querySelector('.maak-delivery__choices');
    this.lockerFields = this.querySelector('[data-locker-fields]');
    this.doorNote = this.querySelector('[data-door-note]');
    this.phoneField = this.querySelector('[data-phone-field]');
    this.status = this.querySelector('[data-status]');
    this.inputs.forEach((input) => {
      input.addEventListener('input', this.#onInput);
      input.addEventListener('blur', () => this.#validate(true));
    });
    this.choices.forEach((choice) => choice.addEventListener('change', this.#onChoice));
    this.#showFields();
    this.#saved = this.#isValid();
    MaakDeliveryDetails.instances.add(this);
    this.#syncButtons();
    // Carts saved before delivery moved to checkout may still carry an old door/locker choice.
    if (this.dataset.stale !== undefined && this.#saved) this.save();
  }

  disconnectedCallback() {
    MaakDeliveryDetails.instances.delete(this);
    syncCheckoutButtons();
  }

  get method() {
    const fixed = this.choices.find((choice) => choice.dataset.fixed !== undefined);
    if (fixed) return fixed.value;
    return this.choices.find((choice) => choice.checked)?.value || '';
  }

  get isLocker() {
    return this.method === this.dataset.lockerMethod;
  }

  /** Inputs that apply to the chosen method. */
  get activeInputs() {
    return this.inputs.filter((input) => this.isLocker || input.dataset.locker === undefined);
  }

  get values() {
    const values = { 'Delivery requested': this.method };
    for (const input of this.inputs) {
      const applies = this.activeInputs.includes(input);
      values[input.dataset.attr] = applies ? input.value.trim() : '';
    }
    values['Recipient cellphone'] = normalisePhone(values['Recipient cellphone'] || '');
    return values;
  }

  #inputOk(input) {
    const value = input.value.trim();
    if (input.dataset.optional === undefined && !value) return false;
    if (input.type === 'tel' && value) return PHONE_PATTERN.test(normalisePhone(value));
    return true;
  }

  #isValid() {
    return Boolean(this.method) && this.activeInputs.every((input) => this.#inputOk(input));
  }

  #validate(showErrors = false) {
    const errors = [];
    const missingChoice = !this.method;
    this.choiceGroup?.setAttribute('aria-invalid', showErrors && missingChoice ? 'true' : 'false');
    if (missingChoice) errors.push(this.choices[0]);
    for (const input of this.inputs) {
      const ok = !this.activeInputs.includes(input) || this.#inputOk(input);
      input.setAttribute('aria-invalid', showErrors && !ok ? 'true' : 'false');
      if (!ok) errors.push(input);
    }
    return errors;
  }

  #showFields() {
    const method = this.method;
    if (this.lockerFields) this.lockerFields.hidden = !this.isLocker;
    if (this.doorNote) this.doorNote.hidden = !method || this.isLocker;
    if (this.phoneField) this.phoneField.hidden = !method;
    this.inputs.forEach((input) => {
      if (input.dataset.locker !== undefined && input.dataset.optional === undefined) input.required = this.isLocker;
    });
  }

  #onChoice = () => {
    this.#showFields();
    this.choiceGroup?.setAttribute('aria-invalid', 'false');
    this.#onInput();
  };

  #onInput = () => {
    this.#saved = false;
    this.#syncButtons();
    this.#setStatus('');
    clearTimeout(this.#saveTimer);
    this.#saveTimer = setTimeout(() => this.save(), 600);
  };

  #setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('maak-delivery__status--error', isError);
  }

  async save() {
    if (!this.#isValid()) {
      this.#saved = false;
      this.#syncButtons();
      return false;
    }
    const values = this.values;
    this.#saving = fetch(`${this.dataset.cartUpdateUrl || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: values }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('save failed');
        this.#saved = true;
        this.#setStatus('Delivery details saved.');
        for (const other of MaakDeliveryDetails.instances) if (other !== this) other.adopt(values);
        return true;
      })
      .catch(() => {
        this.#saved = false;
        this.#setStatus('We could not save your delivery details. Please try again.', true);
        return false;
      })
      .finally(() => {
        this.#saving = null;
        this.#syncButtons();
      });
    return this.#saving;
  }

  adopt(values) {
    for (const choice of this.choices) {
      if (choice.dataset.fixed === undefined) choice.checked = choice.value === values['Delivery requested'];
    }
    for (const input of this.inputs) {
      const value = values[input.dataset.attr];
      if (value) input.value = value;
    }
    this.#showFields();
    this.#saved = true;
    this.#syncButtons();
  }

  get ready() {
    return this.#isValid() && this.#saved;
  }

  async ensureReady() {
    if (this.#saving) await this.#saving;
    if (this.ready) return true;
    const errors = this.#validate(true);
    if (errors.length) {
      this.#setStatus(
        this.method
          ? 'Please complete your delivery details before checkout.'
          : 'Please choose a delivery option before checkout.',
        true
      );
      errors[0].focus();
      return false;
    }
    return this.save();
  }

  #syncButtons() {
    syncCheckoutButtons();
  }
}
MaakDeliveryDetails.instances = new Set();

async function guardCheckout(event) {
  const instances = [...MaakDeliveryDetails.instances].filter((i) => i.isConnected);
  const blockedRules = blockedCharmRules();
  if (!instances.length && !blockedRules.length) return;
  const isCheckout =
    (event.type === 'submit' && (event.submitter?.name === 'checkout' || event.target?.id === 'cart-form')) ||
    (event.type === 'click' && event.target instanceof Element && event.target.closest('[name="checkout"]'));
  if (!isCheckout) return;
  if (blockedRules.length) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const rule = blockedRules.find((el) => el.offsetParent !== null) || blockedRules[0];
    rule.scrollIntoView({ block: 'center' });
    rule.querySelector('[role="alert"]')?.focus();
    return;
  }
  if (instances.every((i) => i.ready)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const visible = instances.find((i) => i.offsetParent !== null) || instances[0];
  await visible.ensureReady();
}

if (!customElements.get('maak-delivery-details')) {
  customElements.define('maak-delivery-details', MaakDeliveryDetails);
  document.addEventListener('submit', guardCheckout, true);
  document.addEventListener('click', guardCheckout, true);
  // Cart sections are re-rendered after quantity changes and removals; re-check the buttons each time.
  new MutationObserver(() => syncCheckoutButtons()).observe(document.body, { childList: true, subtree: true });
  syncCheckoutButtons();
}
